import { sleep } from 'realm-utils/async'
import { log, logErr } from 'realm-utils/log'
import { type CacheOptions, cacheFn, updateFnCache } from '../utils/cache.js'
import { type MatchSpec, matchName } from '../utils/match_name.js'

interface WorkerClient {
  callByName(name: string, method: string, params: unknown[]): Promise<unknown>
}

type Fn = (...args: unknown[]) => unknown | Promise<unknown>

interface UpdateLoopSpec {
  paramsList: unknown[][]
  interval: number
}

interface FunctionToCache {
  match: MatchSpec
  cacheOptions: CacheOptions
  updateCachesInLoop?: UpdateLoopSpec
}

export interface CacheWrapperOptions {
  getWorkerClient: () => WorkerClient
  functionsToCache?: FunctionToCache[]
}

interface PendingUpdate {
  updateCachesInLoop: UpdateLoopSpec
  name: string
  fn: Fn
  cacheOptions: CacheOptions
}

export default ({ getWorkerClient, functionsToCache = [] }: CacheWrapperOptions) => {
  const updates: PendingUpdate[] = []

  const updateCachesLoop = async ({
    updateCachesInLoop,
    name,
    fn,
    cacheOptions,
  }: PendingUpdate): Promise<void> => {
    while (true) {
      try {
        await Promise.all(
          updateCachesInLoop.paramsList.map(async (params: unknown[] = []) => {
            log('update cache start', name, params)

            await updateFnCache(getWorkerClient, fn, params, cacheOptions)

            log('update cache done', name, params)
          }),
        )
      } catch (err: any) {
        logErr('update cache error', name, err, err?.stack)
      }

      await sleep(updateCachesInLoop.interval * 1000)
    }
  }

  return {
    wrapper: (name: string, fn: Fn): Fn => {
      for (let i = 0; i < functionsToCache.length; i++) {
        const { match, cacheOptions, updateCachesInLoop } = functionsToCache[i]

        if (matchName(match, name)) {
          const cacheOptionsWithName: CacheOptions = Object.assign(
            { name: `sapi-${name}` },
            cacheOptions,
          )

          if (updateCachesInLoop) {
            updates.push({ updateCachesInLoop, name, fn, cacheOptions: cacheOptionsWithName })
          }

          return cacheFn(getWorkerClient, fn, cacheOptionsWithName)
        }
      }

      return fn
    },

    loopUpdateCaches: (): void => {
      for (let i = 0; i < updates.length; i++) {
        updateCachesLoop(updates[i])
      }
    },
  }
}
